import { useRef } from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { languages } from '../../../../configs/language'
import { PieChartContainer } from '../../../common/chart/pieChart'
import { SCORE_MODE_ID } from '../../../common/filterCompany/constant'
import { Element } from '../../../common/html/element'
import { Icon } from '../../../common/html/icon'
import { Table } from '../../../common/table'
import { getUnique } from '../../../helpers/common'
import { formatText, formatVal, valToPercent } from '../../../helpers/value'
import { HandleDetail } from '../../common/handleDetail'
import { selectPeriod } from '../../common/period/slice'
import classes from './index.module.scss'

export const ScoreItem = ({ pieName, tableName, data, modelId, colors }) => {
  const chartId = useRef(getUnique())
  const locale = useSelector((state) => state.i18n.locale)
  const period = useSelector(selectPeriod)

  const mapData = data.map((item, index) => {
    const {
      scoreRangeName,
      scoreRange_Min: min,
      scoreRange_Max: max,
      count,
    } = item
    const getRangeText = () => {
      const hasValidRange = min != null && max != null
      if (!hasValidRange) return ''

      if (modelId === SCORE_MODE_ID.CORPORATE_RISK) {
        return min === 0 ? ' (0)' : ` (${min} - ${max})`
      }
      return min ? ` (${min} - ${max})` : ''
    }
    return {
      ...item,
      index,
      name: `${scoreRangeName}${getRangeText()}`,
      value: count,
      color: colors[index],
    }
  })

  return (
    <div className={classes.row}>
      <div className={classes.chart}>
        <Translate value={pieName} className="fw600" />
        <PieChartContainer
          chartId={chartId.current}
          data={mapData}
          width={180}
          height={180}
          innerRadius="55%"
          labelCenter={{
            title: (
              <>
                <div>{data[0]?.total}</div>
                <Translate
                  value="portfolio.summary.COMPANIES"
                  total={data[0]?.total}
                />
              </>
            ),
            outerRadius: '55%',
            style: { color: '#3A414B' },
          }}
          isLabelActive={false}
          renderCustomTooltip={({ payload }) => {
            const item = payload?.[0]?.payload?.payload
            if (item) {
              return (
                <Element
                  className="whiteSpaceNowrap"
                  style={{ color: '#ECECEC' }}
                >
                  <div className="mb1">
                    {formatText(item.scoreRangeName, locale)}
                  </div>
                  <div>
                    {formatVal(item.value, locale, false, 0)}{' '}
                    <Translate value="portfolio.summary.COMPANIES" />
                  </div>
                  <div>
                    {valToPercent(item.percentage, locale, false)}{' '}
                    <Translate value="portfolio.summary.OF_TOTAL_COMPANIES" />
                  </div>
                </Element>
              )
            }
          }}
        />
      </div>

      <div className={classes.table}>
        <Table
          ids={mapData.map((i) => i.index)}
          schema={[
            {
              colId: 'name',
              title: tableName,
              isI18n: true,
              tdStyle: { width: '100%' },
              render: (_, { rowId }) => {
                return (
                  <div className="dFlex alignItemsCenter gap1">
                    <Icon
                      className="icon-square cursorDefault"
                      style={{ fontSize: 13, color: mapData[rowId].color }}
                    />
                    {mapData[rowId].name}
                  </div>
                )
              },
            },
            {
              colId: 'value',
              title: 'portfolio.summary.NUMBER_OF_COMPANIES',
              isI18n: true,
              thStyle: {
                textTransform: locale === languages.vi ? 'capitalize' : '',
              },
              tdStyle: { textAlign: 'right' },
              render: (_, { rowId }) => {
                return (
                  <HandleDetail
                    path="PortfolioInsights/ScoreRangeDetails"
                    otherParams={{
                      timeFrequency: period,
                      scoreRangeId: mapData[rowId].scoreRangeId,
                      modelId,
                    }}
                  >
                    {mapData[rowId].value}
                  </HandleDetail>
                )
              },
            },
            {
              colId: 'percent',
              title: 'portfolio.summary.PERCENT_OF_TOTAL_COMPANIES',
              isI18n: true,
              thStyle: {
                textTransform: locale === languages.vi ? 'capitalize' : '',
              },
              tdStyle: { textAlign: 'right' },
              render: (_, { rowId }) => {
                return (
                  <div>
                    {valToPercent(mapData[rowId].percentage, locale, false)}
                  </div>
                )
              },
            },
          ]}
          autoHeightMax={500}
        />
      </div>
    </div>
  )
}
